// Typed fetch client for the backend (spec §7.2).
// Always sends the custom X-Requested-With header so mutating requests pass the
// backend CSRF check, and credentials so the session cookie rides along.

export type Role = "student" | "trainer" | "admin";

export type User = {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  group_name: string | null;
};

// A saved script (server-side storage, spec §7.2/§7.3).
export type Script = {
  id: number;
  name: string;
  code: string;
  created_at: string;
  updated_at: string;
};

// Lightweight row for the Open → My scripts list (no code body).
export type ScriptListItem = {
  id: number;
  name: string;
  updated_at: string;
};

// --- admin (spec §6.4, §7.4) ---------------------------------------------
export type AdminUser = {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  group_name: string | null;
  is_active: boolean;
  device_check: boolean;
  created_at: string;
  device_count: number;
};

export type Device = {
  id: number;
  user_id: number;
  token: string;
  label: string;
  is_approved: boolean;
  created_at: string;
  last_seen_at: string | null;
};

export type ImportResult = {
  created: string[];
  skipped: { username: string; reason: string }[];
};

export type BulkAction = "deactivate" | "reactivate" | "delete";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "fetch",
      ...(options.headers ?? {}),
    },
    ...options,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : res.statusText;
    throw new ApiError(res.status, detail);
  }

  return body as T;
}

export const api = {
  login(username: string, password: string, deviceToken?: string): Promise<{ user: User }> {
    return request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password, device_token: deviceToken }),
    });
  },
  logout(): Promise<{ ok: boolean }> {
    return request("/auth/logout", { method: "POST" });
  },
  me(): Promise<User> {
    return request("/auth/me");
  },
  changePassword(current_password: string, new_password: string): Promise<{ ok: boolean }> {
    return request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    });
  },

  // --- scripts (spec §7.2) -------------------------------------------------
  listScripts(): Promise<ScriptListItem[]> {
    return request("/scripts");
  },
  getScript(id: number): Promise<Script> {
    return request(`/scripts/${id}`);
  },
  createScript(name: string, code: string): Promise<Script> {
    return request("/scripts", {
      method: "POST",
      body: JSON.stringify({ name, code }),
    });
  },
  updateScript(id: number, patch: { name?: string; code?: string }): Promise<Script> {
    return request(`/scripts/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  },
  deleteScript(id: number): Promise<void> {
    return request(`/scripts/${id}`, { method: "DELETE" });
  },

  // --- admin: users (spec §6.4) -------------------------------------------
  adminListUsers(group?: string): Promise<AdminUser[]> {
    const q = group ? `?group=${encodeURIComponent(group)}` : "";
    return request(`/admin/users${q}`);
  },
  adminCreateUser(payload: {
    username: string;
    display_name: string;
    password: string;
    role: Role;
    group_name: string | null;
  }): Promise<AdminUser> {
    return request("/admin/users", { method: "POST", body: JSON.stringify(payload) });
  },
  adminUpdateUser(
    id: number,
    patch: {
      display_name?: string;
      role?: Role;
      group_name?: string | null;
      is_active?: boolean;
      device_check?: boolean;
      new_password?: string;
    },
  ): Promise<AdminUser> {
    return request(`/admin/users/${id}`, { method: "PUT", body: JSON.stringify(patch) });
  },
  adminDeleteUser(id: number): Promise<void> {
    return request(`/admin/users/${id}`, { method: "DELETE" });
  },
  adminImportUsers(csv_text: string): Promise<ImportResult> {
    return request("/admin/users/import", { method: "POST", body: JSON.stringify({ csv_text }) });
  },
  adminBulk(
    action: BulkAction,
    group: string,
  ): Promise<{ action: string; group: string; affected: number }> {
    return request("/admin/users/bulk", {
      method: "POST",
      body: JSON.stringify({ action, group }),
    });
  },

  // --- admin: devices + settings (spec §7.4) ------------------------------
  adminListDevices(userId: number): Promise<Device[]> {
    return request(`/admin/users/${userId}/devices`);
  },
  adminResetDevices(userId: number): Promise<{ deleted: number }> {
    return request(`/admin/users/${userId}/devices`, { method: "DELETE" });
  },
  adminUpdateDevice(id: number, patch: { label?: string; is_approved?: boolean }): Promise<Device> {
    return request(`/admin/devices/${id}`, { method: "PUT", body: JSON.stringify(patch) });
  },
  adminDeleteDevice(id: number): Promise<void> {
    return request(`/admin/devices/${id}`, { method: "DELETE" });
  },
  adminGetSettings(): Promise<{ device_enforcement: boolean }> {
    return request("/admin/settings");
  },
  adminSetSettings(device_enforcement: boolean): Promise<{ device_enforcement: boolean }> {
    return request("/admin/settings", {
      method: "PUT",
      body: JSON.stringify({ device_enforcement }),
    });
  },
};
